// Api.
import api from '../Base';

// Helper.
import Helper from '../../helpers/Helper';
import { showError } from '../../utils/showAlert';

// Types.
import HostStatistic from '../../types/host/HostStatistic';

// Define a type for the batch result
export interface HostStatsSummary {
    cpu?: number;
    io?: number;
    memory?: number;
}

const StatisticApi = {

    // Modified to accept query string directly
    getStatistic: async (timeRangeQueryString: string, hostId: number, statistic: string): Promise<number | null> => {

        const url = `host/statistic/summary?${timeRangeQueryString}&statistic=${statistic}&id=${hostId}`;

        let data: number | null = null;

        await api.get<HostStatistic[]>(url)
            .then((response) => {
                // Ensure avg is a number before assigning
                if (response.status === 200 && response.data.length > 0 && typeof response.data[0].avg === 'number') {
                    data = response.data[0].avg;
                }
            })
            .catch((error: any) => {
                // Keep data as null on error
                console.error('Failed to retrieve host statistic.', error, timeRangeQueryString, hostId, statistic); // Log query string instead of context
                showError('Failed to retrieve host statistic', error.message || error.toString());
            });

        return data;
    },

    // Modified to accept query string directly
    getHostStatisticsSummaryBatch: async (
        timeRangeQueryString: string, // Accept query string
        hostIds: number[]
    ): Promise<Record<number, HostStatsSummary>> => {
        const statsMap: Record<number, HostStatsSummary> = {};
        if (!hostIds || hostIds.length === 0) {
            return statsMap; // Return empty if no IDs provided
        }

        const uniqueHostIds = Array.from(new Set(hostIds)); // Ensure unique IDs

        // Create promises for all required stats for all unique hosts
        const promises = uniqueHostIds.flatMap(hostId => [
            StatisticApi.getStatistic(timeRangeQueryString, hostId, 'cpuutilisation').then(value => ({ hostId, stat: 'cpu', value })), // Pass query string
            StatisticApi.getStatistic(timeRangeQueryString, hostId, 'ioutilisation').then(value => ({ hostId, stat: 'io', value })), // Pass query string
            StatisticApi.getStatistic(timeRangeQueryString, hostId, 'memoryutilisation').then(value => ({ hostId, stat: 'memory', value })) // Pass query string
        ]);

        try {
            const results = await Promise.all(promises);

            // Process results into the map
            results.forEach(result => {
                if (result.value !== null) { // Only add if data was fetched successfully
                    if (!statsMap[result.hostId]) {
                        statsMap[result.hostId] = {};
                    }
                    statsMap[result.hostId][result.stat as keyof HostStatsSummary] = result.value;
                }
            });

        } catch (error: any) {
            console.error('Failed to retrieve batch host statistics.', error, timeRangeQueryString, uniqueHostIds); // Log query string
            showError('Failed to retrieve batch host statistics', error.message || error.toString());
            // Return potentially partially filled map or empty map on error
        }

        return statsMap;
    },


    // Modified to accept query string directly
    getStatisticPeak: async (timeRangeQueryString: string, hostId: number, statistic: string) => { // Accept query string

        const url = `host/statistic/time?${timeRangeQueryString}&statistic=${statistic}&id=${hostId}`; // Use query string

        let data: number[] = []; // Initialize as empty array

        await api.get<HostStatistic[]>(url)
            .then((response) => {

                if (response.status === 200) {
                    for (let index = 0; index < response.data.length; index++) {

                        data.push(response.data[index].avg);
                    }
                }
            })
            .catch((error: any) => {
                console.error('Failed to retrieve host statistic over time.', error, timeRangeQueryString, hostId, statistic); // Log query string
                showError('Failed to retrieve host statistic over time', error.message || error.toString());
            });
            // Removed empty .then()

        return Math.round(Helper.getPeakValue(data));
    }
}

export default StatisticApi;
